<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Coords } from '~layers/composables/useStorelocator.ts'
import type { TtLocationFragment } from '#graphql-operations'
import { LocationOpeningHours } from '~layers/utils/locationOpeningHours.ts'

// Midden Nederland
const origin = reactive<Coords>({
  lat: 52.1473244,
  lng: 5.2736954,
})

const storeLocationInput = ref()

const { isSearching, visibleLocations, search, hasSearched, userLocation, searchInput, doSearch, metadata } = useStorelocator()

const centerMarker = computed(() => {
  if (metadata.value?.latlng) {
    return {
      lat: metadata.value.latlng.latitude,
      lng: metadata.value.latlng.longitude,
    }
  }
  if (userLocation.value) {
    return userLocation.value
  }
  return null
})

const meta = inject('meta', ref())
const { strapiData, getStrapiData } = useStrapiCms(meta?.value?.strapi_id || '', undefined, 'page', '', meta?.value?.strapi_locale)
const { $t } = useI18n()

await Promise.all([
  getStrapiData(),
  doSearch(undefined),
])

const allStores = [...visibleLocations.value]

const visibleLocationsWithOpeningHours = computed(() => {
  if (!hasSearched.value) {
    return []
  }
  return LocationOpeningHours.fromLocations(visibleLocations.value)
})

const pageTitle = computed(
  () => strapiData.value?.page?.PageTitle || '',
)

const metaTitle = computed(
  () => strapiData.value?.page?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => strapiData.value?.page?.MetaData?.MetaDescription || $t('meta.description'),
)

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: pageTitle.value,
  parents: meta.value.parents,
})

const { pageView } = useDataLayer()
pageView('store-overview', metaTitle.value)

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: strapiData.value?.page?.MetaData?.MetaRobots?.replace('_', ', ') },
  ],
})

const route = useRoute()
const { canonical } = useSeo()
canonical(computed(() => route.path))

const selectedLetter = ref('')
const stores = computed(() => {
  return allStores
    ?.filter((store: TtLocationFragment) => store?.city !== undefined)
    ?.toSorted((a: TtLocationFragment, b: TtLocationFragment) => (a.city || '').localeCompare(b.city || ''))
    ?.filter((store: TtLocationFragment) => {
      if (!selectedLetter.value) {
        return true
      }
      const firstLetter = store.city?.charAt(0)?.toUpperCase()
      return firstLetter === selectedLetter.value
    })
    || []
})

const markers = computed(() => {
  return LocationOpeningHours.fromLocations(stores.value
    ?.filter((store: TtLocationFragment) => store.lat && store.lng))
    .filter(loc => loc.location != null) || []
})

const getMyLocation = async () => {
  if (navigator?.geolocation) {
    userLocation.value = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(
            position.coords
              ? {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                }
              : null,
          )
        },
        (error) => {
          console.error('Error getting location:', error)
          reject(error)
        },
      )
    })

    await nextTick(() => {
      search.value = ''
      doSearch(searchInput.value)
    })
  }
}

const formSearch = () => {
  doSearch(searchInput.value)
}

// const clusterRenderer = {
//   render({ count, position }) {
//     const svg = `<svg fill="#1A2B3C" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="48" height="48">
// <circle cx="48" cy="48" r="40" />
// <circle cx="48" cy="48" opacity="0.5" r="48" />
// <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="32" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
// </svg>`

//     const title = `Cluster of ${count} markers`, zIndex: number = Number(google.maps.Marker.MAX_ZINDEX) + count

//     return new google.maps.Marker({
//       position,
//       zIndex,
//       title,
//       icon: {
//         url: `data:image/svg+xml;base64,${btoa(svg)}`,
//         anchor: new google.maps.Point(24, 24),
//       },
//     })
//   },
// }

const getStoreLink = (store: TtLocationFragment) => {
  const urlKey = normalizeSlug((store as TtLocationFragment & { urlKey?: string }).urlKey || '')
  const city = normalizeSlug(store.city || '')

  return `${route.path}/${urlKey || stringToSlug(city)}`
}

const normalizeSlug = (value: string) => value.trim().replace(/^\/+|\/+$/g, '')

const selectedMarkerId = ref<string | null>(null)

function closeAllInfoWindows() {
  document.querySelectorAll('.gm-ui-hover-effect').forEach(btn => btn.click())
}

const maps = ref()

function handleMarkerClick(id: string, position?: Coords | null) {
  if (position) {
    maps.value?.map?.panTo(position)
  }

  if (selectedMarkerId.value === id) {
    selectedMarkerId.value = null
    return
  }

  closeAllInfoWindows()
  selectedMarkerId.value = id
}

const getInfoWindowHeader = (name: string): HTMLElement => {
  const el = document.createElement('div')
  el.className = 'maps-info-window__title'
  el.textContent = name
  return el
}
</script>

<template>
  <div class="store-overview">
    
<FormKitLazyProvider>
<AppBreadcrumbs v-if="$route.path !== '/'" />

    <div class="store-overview__hero">
      <ScriptGoogleMaps
        ref="maps"
        class="store-overview__map"
        above-the-fold
        trigger="immediate"
        :map-options="{
          center: centerMarker || origin,
          zoom: (centerMarker ? 10 : 8),
          mapTypeControl: false,
          streetViewControl: false,
          cameraControl: false,
        }"
        :center-marker="false"
      >
        <template #placeholder>
          <img
            src="/images/static-map.jpg"
            alt="Static map"
            style="width: 100%; height: 100%; object-fit: cover;"
            height="1920"
            loading="eager"
            fetchpriority="high"
            width="800"
          >
        </template>
        <!-- <ScriptGoogleMapsMarkerClusterer
          :options="{
            renderer: clusterRenderer,
          }"
        > -->
        <ScriptGoogleMapsMarker
          v-for="location in markers"
          :key="location"
          :position="location.location"
          @click="handleMarkerClick(
            String(location.location?.code ?? ''),
            location.location?.lat && location.location?.lng
              ? { lat: location.location.lat, lng: location.location.lng }
              : null,
          )"
        >
          <template #content>
            <img
              src="/images/marker.svg"
              alt=""
              width="32"
              height="39"
              loading="lazy"
            >
          </template>
          <ScriptGoogleMapsInfoWindow
            class="store-overview__info"
            :options="{
              headerContent: getInfoWindowHeader(location.location.name ?? ''),
              ariaLabel: location.location.name,
            }"
            @closeclick="selectedMarkerId = null"
          >
            <div class="store-overview__info-window">
              <div
                v-if="location.today"
                class="maps-info-window__hours"
              >
                <span
                  class="maps-info-window__hours-dot"
                  :class="{
                    'maps-info-window__hours-dot--active': location.today.isOpen,
                    'maps-info-window__hours-dot--inactive': !location.today.isOpen,
                  }"
                />
                <span v-if="!location.today.isOpen">{{ $t('Vandaag gesloten') }}</span>
                <span v-else>
                  {{ $t('Today open') }}
                  <template
                    v-for="(slot, index) in location.today.slots"
                    :key="index"
                  >
                    <template v-if="index > 0"> {{ $t('en') }} </template>
                    {{ slot.open }} {{ '-' }} {{ slot.close }}
                  </template>
                </span>
              </div>

              <div class="maps-info-window__address">
                {{ location.location.locationLine1 }} {{ location.location.locationLine2 }}, {{ location.location.city }}
              </div>

              <div class="maps-info-window__actions">
                <MapRouteButton
                  v-if="location.location?.lat && location.location?.lng"
                  class="maps-info-window__link"
                  :coords="{ lat: location.location.lat, lng: location.location.lng }"
                >
                  <span>{{ $t('Plan your route') }}</span>
                </MapRouteButton>

                <AppLink
                  :to="getStoreLink(location.location as TtLocationFragment)"
                  class="maps-info-window__link"
                >
                  <span>Meer info</span>
                </AppLink>
              </div>
            </div>
          </ScriptGoogleMapsInfoWindow>
        </ScriptGoogleMapsMarker>
        <!-- </ScriptGoogleMapsMarkerClusterer> -->
      </ScriptGoogleMaps>

      <div class="store-overview__container">
        <div class="store-overview__search">
          <UiHeading class="store-overview__heading">
            {{ $t('Find your store') }}
          </UiHeading>

          <UiHeadingSection>
            <div class="store-overview__subheading">
              {{ $t('Personal, inspiring and always close by.') }}
            </div>

            <FormKit
              id="store-overview__form"
              name="store-overview__form"
              type="form"
              form-class="store-overview__form"
              :actions="false"
              @submit="formSearch"
            >
              <FormKit
                id="store-overview__email"
                ref="storeLocationInput"
                v-model="search"
                type="text"
                name="search"
                :label="$t('Location') + ' *'"
                :placeholder="$t('Search by postal code or place name')"
                @input="userLocation = null"
              />
              <button
                v-if="search.length > 0"
                type="button"
                class="store-overview__reset"
                @click="search = ''"
              >
                <NuxtIcon name="icon:close-round" />
              </button>
              <button
                type="submit"
                class="store-overview__button"
              >
                <span>{{ $t('Search locations') }}</span>
                <NuxtIcon name="icon:search" />
              </button>
              <FormKitMessages :node="storeLocationInput?.node" />
            </FormKit>

            <button
              class="store-overview__location"
              type="button"
              @click="getMyLocation"
            >
              <NuxtIcon name="icon:my-location" />
              {{ $t('Use my location') }}
            </button>

            <div
              v-if="isSearching"
              class="store-overview__searching"
            >
              <NuxtIcon
                name="icon:loader"
                class="store-overview__loading"
              />
            </div>
            <div
              v-else-if="visibleLocationsWithOpeningHours?.length"
              class="store-overview__results"
            >
              <article
                v-for="store in visibleLocationsWithOpeningHours.slice(0, 4)"
                :key="store"
                class="store-card"
              >
                <div class="store-card__item">
                  <div class="store-card__content">
                    <AppLink
                      :to="getStoreLink(store.location as TtLocationFragment)"
                      class="store-card__link store-card__title"
                    >
                      {{ store.location?.name }}
                    </AppLink>
                    <div class="store-card__address">
                      {{ store.location?.locationLine1 }} {{ store.location?.locationLine2 }}, {{ store.location?.city }}
                    </div>
                    <MapRouteButton
                      v-if="store.location?.lat && store.location?.lng"
                      class="store-card__route"
                      :coords="{ lat: store.location.lat, lng: store.location.lng }"
                    >
                      <span>{{ $t('Plan your route') }}</span>
                    </MapRouteButton>
                    <div
                      v-if="store.today"
                      class="store-card__hours"
                    >
                      <span :class="{ 'store-card__hours-dot--active': store.today.isOpen, 'store-card__hours-dot--inactive': !store.today.isOpen, 'store-card__hours-dot': true }" />
                      <span v-if="!store.today.isOpen">{{ $t('Vandaag gesloten') }}</span>
                      <span v-else>
                        {{ $t('Vandaag geopend') }}
                        <template
                          v-for="(slot, index) in store.today.slots"
                          :key="index"
                        >
                          <template v-if="index > 0"> {{ $t('en') }} </template>
                          {{ slot.open }} {{ $t('tot') }} {{ slot.close }}
                        </template>
                      </span>
                    </div>
                  </div>
                  <NuxtIcon
                    name="icon:arrow"
                    class="store-card__arrow"
                  />
                </div>
              </article>
            </div>
            <div
              v-else-if="hasSearched"
              class="store-overview__no-results"
            >
              {{ $t('No locations found. Please try another search.') }}
            </div>
          </UiHeadingSection>
        </div>
      </div>
    </div>

    <UiHeadingSection>
      <StrapiSlice
        class="store-overview__stores"
        :slice="{
          Settings: {
            BackgroundColor: '#fff',
            SliceWidth: 'large-column',
            Title: $t('Search by place name'),
          },
        }"
        :index="0"
        variant="horizontal"
      >
        <div class="alphabet">
          <button
            type="button"
            class="alphabet__button"
            :class="{ 'alphabet__button--active': !selectedLetter }"
            @click="selectedLetter = ''"
          >
            {{ $t('All') }}
          </button>
          <button
            v-for="letter in 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'"
            :key="letter"
            type="button"
            class="alphabet__button"
            :class="{ 'alphabet__button--active': selectedLetter === letter }"
            @click="selectedLetter = letter"
          >
            {{ letter }}
          </button>
        </div>

        <ul
          v-if="stores?.length"
          class="store-overview__list"
        >
          <li
            v-for="store in stores"
            :key="`store-${store.code}`"
            class="store-overview__item"
          >
            <AppLink
              :to="getStoreLink(store)"
              class="store-overview__link"
            >
              {{ store.city }}
            </AppLink>
            <NuxtIcon
              name="icon:arrow"
              class="store-overview__link__arrow"
            />
          </li>
        </ul>
      </StrapiSlice>

      <StrapiSlices
        v-if="strapiData?.page?.Slices?.length"
        :slices="strapiData.page.Slices"
        :slice-offset="1"
      />
    </UiHeadingSection>
</FormKitLazyProvider>


    <!--  -->
  </div>
</template>

<style>
@import url("~layers/pages/~store-overview.css");
</style>
